import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { addSeconds, format, getUnixTime, startOfDay } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { restartableTask } from 'ember-concurrency';
import type DeskModel from 'garaje/models/desk';
import type MapFloorModel from 'garaje/models/map-floor';
import type WorkplaceDayModel from 'garaje/models/workplace-day';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LoggerService from 'garaje/services/logger';
import { SECONDS_IN_A_DAY } from 'garaje/utils/enums';
import urlBuilder from 'garaje/utils/url-builder';

type Phase = 'desk-selection' | 'move-details';

type NextConflictKind = 'assigned' | 'restricted' | 'blocked';

interface NextConflictInfo {
  kind: NextConflictKind;
  startDate: Date;
  assigneeName?: string;
  neighborhoodName?: string;
}

interface NextConflictApiData {
  kind: 'ASSIGNED_TO_OTHER' | 'RESTRICTED_NEIGHBORHOOD' | 'BLOCKED';
  startTime: string;
  assignmentId?: number;
  assigneeName?: string | null;
  neighborhoodName?: string | null;
}

interface NextConflictApiResponse {
  data: NextConflictApiData | null;
}

interface CurrentDeskInfo {
  name: string;
  floorName: string | null;
  neighborhoodName: string | null;
}

interface MoveRequestModalArgs {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  currentDesk: CurrentDeskInfo | null;
  hasCurrentDesk: boolean;
  floors: MapFloorModel[];
  currentFloorId: string | null;
  employeeId: string;
  employeeEmail: string;
  locationId: string;
  timezone: string;
  workplaceDays?: WorkplaceDayModel[];
  selectedDate?: Date;
}

export default class MoveRequestModalComponent extends Component<{ Args: MoveRequestModalArgs }> {
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;
  @service declare logger: LoggerService;
  @service declare store: Store;

  @tracked phase: Phase = 'desk-selection';
  @tracked selectedDesk: DeskModel | null = null;
  @tracked selectedFloorId: string | null = null;
  @tracked moveDate: Date | null = null;
  @tracked moveRequestReason = '';
  @tracked isSubmitting = false;
  @tracked conflictAssignee: string | null = null;
  @tracked conflictNeighborhood: string | null = null;
  @tracked conflictBlocked = false;
  @tracked nextConflict: NextConflictInfo | null = null;

  get isDeskSelectionPhase(): boolean {
    return this.phase === 'desk-selection';
  }

  get isMoveDetailsPhase(): boolean {
    return this.phase === 'move-details';
  }

  get canProceedToDetails(): boolean {
    return this.selectedDesk !== null;
  }

  get canSubmit(): boolean {
    return this.moveDate != null && this.moveDate >= this.minMoveDate && this.moveRequestReason.trim().length > 0;
  }

  get hasConflict(): boolean {
    return this.conflictAssignee != null || this.conflictNeighborhood != null || this.conflictBlocked;
  }

  get selectedDeskDisplayText(): string {
    if (!this.selectedDesk) return '';
    const name = this.selectedDesk.name;
    const floorId = this.selectedDesk.belongsTo('floor').id();
    const floor = floorId ? this.selectedDesk.store.peekRecord('floor', floorId) : null;
    return [name, floor?.name].filter(Boolean).join(', ');
  }

  get selectedDeskNeighborhood(): string | null {
    return this.selectedDesk?.neighborhoodName ?? null;
  }

  get currentDeskDisplayText(): string {
    if (!this.args.currentDesk) return '';
    return [this.args.currentDesk.name, this.args.currentDesk.floorName].filter(Boolean).join(', ');
  }

  get currentDeskNeighborhood(): string | null {
    return this.args.currentDesk?.neighborhoodName ?? null;
  }

  get buttonText(): string {
    return this.args.hasCurrentDesk ? 'Request desk change' : 'Request desk assignment';
  }

  get title(): string {
    return this.args.hasCurrentDesk ? 'Request desk change' : 'Request desk assignment';
  }

  /**
   * Returns the working hours start time (e.g., "08:00:00") for the selected move date,
   * or "00:00:00" if no active workplace day is configured for that day.
   */
  get moveDateStartTime(): string {
    if (!this.moveDate) return '00:00:00';
    const dayOfWeek = format(this.moveDate, 'EEEE');
    const activeDay = this.args.workplaceDays?.find((d) => d.active && d.dayOfWeek === dayOfWeek);
    return activeDay?.startTime ?? '00:00:00';
  }

  get activeFloorId(): string | null {
    return this.selectedFloorId ?? this.args.currentFloorId;
  }

  // Start of today in the location's timezone, as a UTC instant. Used as the
  // picker's minDate so users can't request a move for a date already in the past
  // for that location, even if the browser's local clock is ahead/behind.
  get minMoveDate(): Date {
    const nowInLocationTz = toZonedTime(new Date(), this.args.timezone);
    const startOfTodayInLocationTz = startOfDay(nowInLocationTz);
    return fromZonedTime(startOfTodayInLocationTz, this.args.timezone);
  }

  /**
   * Returns the move date if set (phase 2), otherwise falls back to the
   * live map's selected date so desk availability is checked against the
   * date the user is currently viewing.
   */
  get activeMoveDate(): Date | undefined {
    return this.moveDate ?? this.args.selectedDate;
  }

  @action
  handleFloorChange(floorId: string): void {
    this.selectedFloorId = floorId;
  }

  @action
  handleDeskSelect(desk: DeskModel | null): void {
    this.selectedDesk = desk;
  }

  @action
  goToNextPhase(): void {
    if (!this.canProceedToDetails) return;
    this.phase = 'move-details';

    // Pre-fill the move date with the date used for Phase 1's desks API call
    // so the picker isn't empty and the conflict can be derived immediately.
    // Clamp to the floor: args.selectedDate may be stale if the user opened
    // phase 1 before midnight in the location's tz and crossed it before reaching here.
    if (this.moveDate == null && this.args.selectedDate != null) {
      const floor = this.minMoveDate;
      this.moveDate = this.args.selectedDate < floor ? floor : this.args.selectedDate;
    }

    // The selected DeskModel was decorated by the map's loadDesksTask against
    // the same date (activeMoveDate === moveDate), so derive the conflict
    // synchronously from the in-memory record — no re-fetch needed.
    if (this.selectedDesk) {
      this.deriveConflictFromDesk(this.selectedDesk);
      this.runNextConflictCheckIfNoConflict();
    }
  }

  @action
  goToPreviousPhase(): void {
    // Cancel any in-flight conflict check so a late response can't overwrite
    // the synchronous state we'll compute on the next goToNextPhase. Also
    // null out `nextConflict` so a desk swap on phase 1 doesn't render the
    // previous desk's stale future-conflict banner.
    void this.checkDeskConflictTask.cancelAll();
    this.clearNextConflict();
    this.phase = 'desk-selection';
  }

  @action
  handleClose(): void {
    this.resetState();
    this.args.onClose();
  }

  @action
  updateMoveDate(date: Date | null): void {
    if (date) {
      // Null + cancel the previous date's findNextConflictTask BEFORE
      // re-querying — checkDeskConflictTask doesn't restart findNextConflictTask
      // directly (it triggers it later via runNextConflictCheckIfNoConflict),
      // leaving a window where the old fetch can resolve and write stale data.
      this.clearNextConflict();
      this.moveDate = date;
      void this.checkDeskConflictTask.perform();
    }
  }

  checkDeskConflictTask = restartableTask(async () => {
    if (!this.selectedDesk || !this.moveDate) {
      this.clearConflict();
      return;
    }

    const dayStart = startOfDay(this.moveDate);
    const desks: DeskModel[] = (
      await this.store.query('desk', {
        filter: {
          id: this.selectedDesk.id,
          'location-id': this.args.locationId,
          'available-from': getUnixTime(dayStart),
          'available-to': getUnixTime(addSeconds(dayStart, SECONDS_IN_A_DAY - 1)),
        },
        fields: { desks: 'accessible' },
      })
    ).slice();

    const desk = desks[0];
    if (!desk) {
      this.clearConflict();
      this.runNextConflictCheckIfNoConflict();
      return;
    }

    this.deriveConflictFromDesk(desk);
    this.runNextConflictCheckIfNoConflict();
  });

  /**
   * If no same-day conflict was found, look forward for the nearest future
   * conflict on this desk and surface it. Cancelled if the user changes the
   * date or returns to phase 1.
   */
  findNextConflictTask = restartableTask(async () => {
    if (!this.selectedDesk || !this.moveDate) {
      this.nextConflict = null;
      return;
    }

    // start-of-day in the LOCATION's tz (not the browser's), as a UTC instant
    // — envoy-spaces' next-conflict endpoint requires offset-aware ISO-8601.
    const dayStartLocalAsUtc = startOfDay(toZonedTime(this.moveDate, this.args.timezone));
    const fromUtcIso = fromZonedTime(dayStartLocalAsUtc, this.args.timezone).toISOString();
    const url = urlBuilder.moveRequests.nextConflictUrl(this.selectedDesk.id, this.args.employeeId, fromUtcIso);

    let response: NextConflictApiResponse;
    try {
      response = await this.ajax.request<NextConflictApiResponse>(url);
    } catch {
      // 404, network error, or backend not yet live — treat as "no future
      // conflict" so the existing flow isn't disrupted during rollout.
      this.nextConflict = null;
      return;
    }

    const data = response?.data ?? null;
    if (!data) {
      this.nextConflict = null;
      return;
    }

    this.nextConflict = {
      kind: mapApiConflictKind(data.kind),
      startDate: new Date(data.startTime),
      assigneeName: data.assigneeName ?? undefined,
      neighborhoodName: data.neighborhoodName ?? undefined,
    };
  });

  private runNextConflictCheckIfNoConflict(): void {
    if (this.hasConflict) {
      this.nextConflict = null;
      void this.findNextConflictTask.cancelAll();
      return;
    }
    void this.findNextConflictTask.perform();
  }

  /**
   * Compute conflict flags from a date-scoped DeskModel record. The desk
   * must already have `enabled`, `assignedTo`, and `neighborhood` decorated
   * for the target move date (which is what Phase 1's loadDesksTask produces).
   */
  private deriveConflictFromDesk(desk: DeskModel): void {
    // Blocked: desk is disabled on the move date
    this.conflictBlocked = desk.enabled === false;

    // Restricted: neighborhood has an employee allow-list that excludes the requester
    const neighborhoodId = desk.belongsTo('neighborhood').id();
    this.conflictNeighborhood = null;
    if (neighborhoodId) {
      const neighborhood = this.store.peekRecord('neighborhood', neighborhoodId);
      const allowList = neighborhood?.employeeIds ?? [];
      if (allowList.length > 0 && !allowList.includes(this.args.employeeId)) {
        this.conflictNeighborhood = neighborhood?.name ?? neighborhoodId;
      }
    }

    // Assigned: someone other than the requester holds the desk on the move date
    const assignedTo = desk.assignedTo;
    if (assignedTo && assignedTo !== this.args.employeeEmail) {
      const employee = this.store.peekAll('employee').find((e) => e.email === assignedTo);
      this.conflictAssignee = employee?.name ?? assignedTo;
    } else {
      this.conflictAssignee = null;
    }
  }

  private clearConflict(): void {
    this.conflictAssignee = null;
    this.conflictNeighborhood = null;
    this.conflictBlocked = false;
  }

  private clearNextConflict(): void {
    this.nextConflict = null;
    void this.findNextConflictTask.cancelAll();
  }

  @action
  updateMoveRequestReason(event: Event): void {
    this.moveRequestReason = (event.target as HTMLInputElement).value;
  }

  @action
  async submitRequest(): Promise<void> {
    if (this.isSubmitting || !this.selectedDesk) return;

    // Floor advances at midnight in the location tz; moveDate may have been
    // set when the floor was lower (user lingered across midnight, or hit
    // Back→Next after rollover). canSubmit's >= floor check is reactive on
    // tracked deps only — Date.now() isn't tracked — so the button can stay
    // cached as enabled even after the floor passes. Clamp here BEFORE the
    // canSubmit guard so the guard sees the corrected value.
    if (this.moveDate != null) {
      const floor = this.minMoveDate;
      if (this.moveDate < floor) {
        this.moveDate = floor;
      }
    }

    if (!this.canSubmit) return;

    this.isSubmitting = true;
    try {
      // moveDateTime is in the location's local timezone (passed separately as `timezone`).
      // The backend interprets this datetime using the timezone field.
      const moveDateTime = `${format(this.moveDate!, 'yyyy-MM-dd')}T${this.moveDateStartTime}`;
      await this.ajax.request(urlBuilder.moveRequests.createUrl(), {
        type: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
          employeeId: Number(this.args.employeeId),
          deskId: Number(this.selectedDesk?.id),
          locationId: Number(this.args.locationId),
          moveDateTime,
          timezone: this.args.timezone,
          reason: this.moveRequestReason.trim(),
        }),
      });
      this.resetState();
      this.args.onSubmitted();
    } catch (error) {
      this.logger.error(error instanceof Error ? error : new Error('Failed to create move request'));
      this.flashMessages.showAndHideFlash('error', 'Unable to create move request. Please try again.', {
        showAboveModalOverlay: true,
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  private resetState(): void {
    this.phase = 'desk-selection';
    this.selectedDesk = null;
    this.selectedFloorId = null;
    this.moveDate = null;
    this.moveRequestReason = '';
    this.isSubmitting = false;
    this.clearConflict();
    this.clearNextConflict();
  }
}

function mapApiConflictKind(apiKind: NextConflictApiData['kind']): NextConflictKind {
  switch (apiKind) {
    case 'ASSIGNED_TO_OTHER':
      return 'assigned';
    case 'RESTRICTED_NEIGHBORHOOD':
      return 'restricted';
    case 'BLOCKED':
      return 'blocked';
  }
}
